import { useAtom } from "jotai";
import { useAction, useMutation, useQuery } from "convex/react";

import { useSession } from "@/core/auth/useSession";
import {
  Check,
  Copy,
  Headphones,
  Image as ImageIcon,
  Key,
  Loader2,
  Trash2,
} from "@/shared/ui/icons";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { settingsModalOpenAtom } from "@/core/state";
import {
  Badge,
  Avatar,
  Button,
  Carte,
  Champ,
  Infobulle,
  Ligne,
  Lignes,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Onglets,
  Section,
  useConfirmer,
} from "@/shared/ui";

/**
 * Settings modal — popup tabbed inspirée d'Axiom / DEX Screener.
 *
 * Trigger :
 *   - User badge (TopBar) → menu deroulant → "Settings"
 *
 * Tabs : Account / Organization / Security.
 *
 * Dessin (coque, 01-11-2026) : `Onglets` en ligne, `Section` / `Carte` pour les
 * groupes, `Champ` pour le pseudo, `Lignes` pour les infos et les actions,
 * `useConfirmer` pour la suppression du compte — plus de contour gris ni de
 * carte écrite à la main.
 *
 * Pattern :
 *   - Le state edite est lifte ici (`draft`) → permet un Save/Cancel global
 *     en footer sticky qui agit sur le tab actif.
 *   - Les actions destructives (delete, reset cache) sont des handlers
 *     directs — pas de save/cancel, ce sont des operations atomiques.
 *   - Les uploads d'images (avatar user, logo org) sont aussi atomiques :
 *     pick file → upload → setAvatar/setLogo en chaine, sans passer par le
 *     save button (sinon il faudrait gérer un draft binaire complexe).
 */
type TabId = "account" | "organization" | "security";

interface TabDef {
  id: TabId;
  /** True si le tab a un formulaire editable → afficher Save/Cancel footer. */
  editable: boolean;
}

const TABS: TabDef[] = [
  { id: "account",      editable: true  },
  // Organization : pas de Save footer. Toutes les actions sont atomiques
  // (logo upload, create org), il n'y a plus de rename (2026-05-27 :
  // un user ne peut plus renommer son org via cette UI).
  { id: "organization", editable: false },
  { id: "security",     editable: false },
  // 🪦 Notifications et Préférences retirés le 07-10-2026 (Kayla : « retire
  // la page Notification […] et il retire la page de préférence aussi »).
  // Thème, fond, taille et les réglages avancés vivent dans « Personnaliser »
  // (rail gauche, cf ThemePickerModal).
];

/**
 * « Contacter le support » = ouvrir un ticket sur le Discord (Kayla,
 * 07-10-2026). Le lien mène au salon #ouvrir-un-ticket du serveur ; Discord
 * l'ouvre dans l'application si elle est installée, sur le web sinon. Les
 * tickets y sont lus par Francis (cf convex/ticketsAgent.ts).
 */
const SALON_DES_TICKETS =
  "https://discord.com/channels/1525799073331024013/1525803676835381369";

export function SettingsModal() {
  const { t } = useTranslation();
  const [open, setOpen] = useAtom(settingsModalOpenAtom);
  const [tab, setTab] = useState<TabId>("account");
  const me = useQuery(api.users.me);
  const updateName = useMutation(api.users.updateName);
  const refreshWorkOSDetails = useAction(api.users.refreshWorkOSDetails);

  // À l'ouverture du modal on rafraîchit les métadonnées WorkOS (last sign in,
  // member since, provider) — best-effort, on swallow les erreurs pour ne pas
  // bloquer l'affichage.
  useEffect(() => {
    if (!open) return;
    refreshWorkOSDetails().catch((err) => {
      console.error("refreshWorkOSDetails failed:", err);
    });
  }, [open, refreshWorkOSDetails]);

  const initialPseudo = me?.name ?? "";

  const [draftPseudo, setDraftPseudo] = useState<string>(initialPseudo);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDraftPseudo(initialPseudo);
      setSaveError(null);
    }
  }, [open, initialPseudo]);

  const dirty = useMemo(
    () => draftPseudo !== initialPseudo,
    [draftPseudo, initialPseudo],
  );

  const activeTabDef = TABS.find((def) => def.id === tab)!;
  const showSaveFooter = activeTabDef.editable;

  async function handleSave() {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (draftPseudo !== initialPseudo && draftPseudo.trim().length > 0) {
        await updateName({ name: draftPseudo.trim() });
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setDraftPseudo(initialPseudo);
    setSaveError(null);
  }

  return (
    <Modal
      isOpen={open}
      onClose={() => setOpen(false)}
      size="xl"
      zIndex={400}
      ariaLabel={t("shell.settings.ariaLabel")}
      className="h-[85dvh] sm:h-[640px]"
    >
      <ModalHeader
        title={t("shell.settings.title")}
        onClose={() => setOpen(false)}
        closeLabel={t("common.close")}
      />

      {/* Les onglets : sur un téléphone étroit la rangée défile (c'est `Onglets`). */}
      <div className="shrink-0 px-1">
        <Onglets
          nom={t("shell.settings.tabsAria")}
          options={TABS.map((def) => ({
            valeur: def.id,
            etiquette: t(`shell.settings.tabs.${def.id}`),
          }))}
          valeur={tab}
          onChange={setTab}
        />
      </div>

      <ModalBody padding="px-4 py-4">
        {tab === "account" && (
          <AccountTab pseudo={draftPseudo} onPseudoChange={setDraftPseudo} />
        )}
        {tab === "organization" && <OrganizationTab />}
        {tab === "security" && <SecurityTab />}
      </ModalBody>

      {/* Footer Save/Cancel — visible uniquement sur les tabs editables */}
      {showSaveFooter && (
        <ModalFooter className="flex-wrap">
          <StatutDeLEnregistrement erreur={saveError}>
            {saveError
              ? saveError
              : dirty
                ? t("shell.settings.unsaved")
                : t("shell.settings.allSaved")}
          </StatutDeLEnregistrement>
          <Button
            variant="secondary"
            size="lg"
            onClick={handleCancel}
            disabled={!dirty || saving}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleSave}
            disabled={!dirty || saving}
            icon={
              saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )
            }
          >
            {t("common.saveChanges")}
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
}

/** Le mot du pied (« Toutes les modifications enregistrées »), l'erreur en rouge et en infobulle quand elle est tronquée. */
function StatutDeLEnregistrement({
  erreur,
  children,
}: {
  erreur: string | null;
  children: ReactNode;
}) {
  const texte = (
    <span
      className={
        "min-w-0 basis-full text-[13px] sm:mr-auto sm:basis-auto sm:truncate " + (erreur ? "text-erreur" : "text-muted")
      }
    >
      {children}
    </span>
  );
  return erreur ? <Infobulle texte={erreur}>{texte}</Infobulle> : texte;
}

/* ============================================================ */
/* TABS                                                            */
/* ============================================================ */

interface AccountTabProps {
  pseudo: string;
  onPseudoChange: (next: string) => void;
}

function AccountTab({ pseudo, onPseudoChange }: AccountTabProps) {
  const { t, i18n } = useTranslation();
  const { user } = useSession();
  const me = useQuery(api.users.me);
  const uploadAvatar = useAction(api.users.uploadAvatar);
  const clearAvatar = useAction(api.users.clearAvatar);

  const email = user?.email ?? "—";
  const avatarUrl = me?.avatarUrl ?? user?.profilePictureUrl ?? null;
  const hasCustomAvatar = me?.hasCustomAvatar ?? false;
  const workosUserId = user?.workosUserId ?? "—";

  const lastSignInAt = formatDateTime(me?.workosLastSignInAt ?? null, i18n.resolvedLanguage);
  const memberSince = formatDate(me?.workosCreatedAt ?? null, i18n.resolvedLanguage);
  const provider = me?.signInProvider ?? "";

  return (
    <div className="flex flex-col gap-5">
      {/* Hero card : avatar XL + pseudo */}
      <Carte remplissage="lg">
        <div className="flex items-start gap-5">
          <ImageUploader
            currentUrl={avatarUrl}
            fallback={pseudo || email}
            isCustom={hasCustomAvatar}
            onUpload={async (file) => {
              const bytes = await file.arrayBuffer();
              await uploadAvatar({ bytes, contentType: file.type });
            }}
            onClear={async () => {
              await clearAvatar();
            }}
            ariaLabel={t("shell.settings.account.avatarAria")}
            uploadHint={t("shell.settings.account.avatarUploadHint")}
            clearLabel={t("shell.settings.account.avatarClear")}
          />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate text-[22px] font-medium leading-tight text-fg">
                {pseudo || "—"}
              </span>
              {provider && <Badge ton="gris">{provider}</Badge>}
            </div>
            <span className="truncate text-[14px] text-muted">{email}</span>
            <span className="text-[13px] text-faint">
              {t("shell.settings.account.lastSignIn", { time: lastSignInAt })}
            </span>
          </div>
        </div>
      </Carte>

      {/* Profile editable */}
      <Section
        titre={t("shell.settings.account.profile")}
        sousTitre={t("shell.settings.account.profileDescription")}
      >
        <div className="flex flex-col gap-4 pt-1">
          <Champ
            etiquette={t("shell.settings.account.pseudo")}
            type="text"
            value={pseudo}
            onChange={(e) => onPseudoChange(e.target.value)}
            placeholder={t("shell.settings.account.pseudoPlaceholder")}
            maxLength={80}
          />
          <Champ
            etiquette={t("shell.settings.account.email")}
            type="email"
            value={email}
            disabled
          />
        </div>
      </Section>

      {/* Account metadata read-only */}
      <Section
        titre={t("shell.settings.account.info")}
        sousTitre={t("shell.settings.account.infoDescription")}
        remplissage="aucun"
      >
        <Lignes>
          <ReadOnlyRow label={t("shell.settings.account.userId")} value={workosUserId} copyable />
          <ReadOnlyRow
            label={t("shell.settings.account.signInMethod")}
            value={provider || "—"}
            accent
          />
          <ReadOnlyRow label={t("shell.settings.account.lastSignInLabel")} value={lastSignInAt} />
          <ReadOnlyRow label={t("shell.settings.account.memberSince")} value={memberSince} />
        </Lignes>
      </Section>
    </div>
  );
}

function formatDateTime(iso: string | null, lang: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(lang ?? undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDate(iso: string | null, lang: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(lang ?? undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Tab Organisation — refacto 2026-05-28.
 *
 * Affiche la liste de TOUTES les organisations où l'user est membre (nom +
 * logo + role + badge Founder pour celle qu'il a créée). Pour l'org dont il
 * est founder, il peut uploader/clear le logo. Le nom de l'org N'EST PAS
 * éditable depuis cette UI (décidé 2026-05-27 : seul le logo bouge).
 *
 * Bouton "Créer mon organisation" :
 *   - Affiché si l'user n'est founder d'AUCUNE org
 *   - Masqué sinon (règle "1 founder per user")
 *
 * Pas de section "rejoindre une organisation" : les invitations passent par
 * email WorkOS, l'user clique le lien — pas de self-join via cette UI.
 *
 * Note 2026-05-28 : les informations de facturation (raison sociale, adresse,
 * pays, etc.) ont été déplacées hors de ce tab vers l'onglet "Settings" de
 * l'OrganisationDetailPanel — chaque org édite ses propres infos dans son
 * propre panel, pas dans le SettingsModal global du user.
 */
function OrganizationTab() {
  const { t } = useTranslation();
  const accounts = useQuery(api.accounts.listMine);
  const uploadLogo = useAction(api.accounts.uploadLogo);
  const clearLogo = useAction(api.accounts.clearLogo);

  if (accounts === undefined) {
    return (
      <div className="flex h-full items-center justify-center text-center text-muted">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  // Aucune org → l'user n'a jamais finalisé son onboarding, il n'est pas
  // censé voir le SettingsModal. Cas edge, on lui propose de créer.
  if (accounts.length === 0) {
    // Création d'organisation désactivée (2026-06-30) : plus de bouton "Créer".
    // Un user sans org doit être invité dans Xeyla.
    return (
      <div className="flex flex-col gap-4">
        <p className="text-[14px] text-muted">
          {t("shell.settings.organization.noAccount")}
        </p>
      </div>
    );
  }

  // Bouton "Créer mon organisation" retiré (2026-06-30) — plateforme
  // restreinte à l'unique organisation. Le verrou serveur vit dans
  // accounts.createMyOrganization (ORG_CREATION_DISABLED).
  return (
    <div className="flex flex-col gap-5">
      <Section
        titre={t("shell.settings.organization.myOrgs")}
        sousTitre={t("shell.settings.organization.myOrgsDescription")}
        remplissage="aucun"
      >
        <Lignes>
          {accounts.map((acc) => (
            <OrgRow
              key={acc._id}
              account={acc}
              uploadLogo={uploadLogo}
              clearLogo={clearLogo}
            />
          ))}
        </Lignes>
      </Section>
    </div>
  );
}

function OrgRow({
  account,
  uploadLogo,
  clearLogo,
}: {
  account: {
    _id: Id<"accounts">;
    name: string;
    logoUrl: string | null;
    role: string;
    roleSlug: string | null;
    isFounder: boolean;
  };
  uploadLogo: (args: {
    accountId: Id<"accounts">;
    bytes: ArrayBuffer;
    contentType: string;
  }) => Promise<unknown>;
  clearLogo: (args: { accountId: Id<"accounts"> }) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  // Seul le founder peut éditer le logo. Les autres orgs sont read-only
  // depuis cette UI (les non-founders peuvent toujours toucher au logo via
  // un panneau Team dédié — c'est l'UI Settings qui est volontairement
  // restreinte ici).
  const canEditLogo = account.isFounder;
  const roleLabel = account.roleSlug
    ? t(`panels.team.roles.${account.roleSlug}` as never, {
        defaultValue: account.roleSlug,
      })
    : t(`panels.team.roles.${account.role}` as never, {
        defaultValue: account.role,
      });

  return (
    <Ligne
      avant={
        <ImageUploader
          currentUrl={account.logoUrl ?? null}
          fallback={account.name}
          isCustom={!!account.logoUrl}
          disabled={!canEditLogo}
          onUpload={async (file) => {
            const bytes = await file.arrayBuffer();
            await uploadLogo({
              accountId: account._id,
              bytes,
              contentType: file.type,
            });
          }}
          onClear={async () => {
            await clearLogo({ accountId: account._id });
          }}
          ariaLabel={t("shell.settings.organization.logoAria")}
          uploadHint={t("shell.settings.organization.logoUploadHint")}
          clearLabel={t("shell.settings.organization.logoClear")}
          shape="square"
        />
      }
      etiquette={
        <span className="flex flex-wrap items-center gap-2">
          <span className="truncate text-[16px] font-medium">{account.name}</span>
          {account.isFounder && (
            <Badge ton="marque" title={t("panels.team.rbac.founderBadgeTitle")}>
              {t("panels.team.rbac.founderBadge")}
            </Badge>
          )}
        </span>
      }
      sous={
        <span className="block whitespace-normal">
          {roleLabel}
          {!canEditLogo && (
            <span className="mt-1 block text-[12px] text-faint">
              {t("shell.settings.organization.logoFounderOnly")}
            </span>
          )}
        </span>
      }
    />
  );
}

function SecurityTab() {
  const { t } = useTranslation();
  const { signOut } = useSession();
  const requestPasswordReset = useAction(api.users.requestPasswordReset);
  const deleteMyAccount = useAction(api.users.deleteMyAccount);
  const impact = useQuery(api.users.getDeletionImpact);
  // Les deux questions de la suppression : `confirmer` rend une promesse, `dialogue`
  // se pose une fois dans le rendu.
  const { confirmer, dialogue } = useConfirmer();

  // États inline (pas de toast/notification system global dans le cockpit) :
  // - resetState : status du bouton "Change password"
  // - deleteState : status du bouton "Delete account"
  const [resetState, setResetState] = useState<
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "sent"; email: string }
    | { kind: "error"; message: string }
  >({ kind: "idle" });
  const [deleteState, setDeleteState] = useState<
    { kind: "idle" } | { kind: "loading" } | { kind: "error"; message: string }
  >({ kind: "idle" });

  const ownedAgencies = impact?.ownedAgencies ?? [];
  const hasOwnedAgencies = ownedAgencies.length > 0;

  async function handleResetPassword() {
    if (resetState.kind === "loading") return;
    setResetState({ kind: "loading" });
    try {
      const { email } = await requestPasswordReset();
      setResetState({ kind: "sent", email });
    } catch (err) {
      setResetState({
        kind: "error",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  // Si owner d'une ou plusieurs agences, on passe par 2 questions :
  //   « es-tu sûr ? » → « ton agence va être supprimée » → action.
  // Sinon une seule étape suffit.
  async function handleDelete() {
    const okCompte = await confirmer({
      titre: t("shell.settings.security.deleteAccount.confirmTitle"),
      texte: t("shell.settings.security.deleteAccount.confirmMessage"),
      confirmer: hasOwnedAgencies
        ? t("common.continue")
        : t("shell.settings.security.deleteAccount.confirmAction"),
      annuler: t("common.cancel"),
      closeLabel: t("common.close"),
      danger: true,
    });
    if (!okCompte) return;
    if (hasOwnedAgencies) {
      const okAgences = await confirmer({
        titre: t("shell.settings.security.deleteAccount.agencyTitle"),
        texte: (
          <div className="flex flex-col gap-2">
            <p>
              {t("shell.settings.security.deleteAccount.agencyMessage", {
                count: ownedAgencies.length,
              })}
            </p>
            <ul className="list-disc pl-4 text-fg">
              {ownedAgencies.map((a) => (
                <li key={a.name}>
                  <span className="font-medium">{a.name}</span>
                  {a.memberCount > 1 && (
                    <span className="ml-1 text-faint">
                      {t("shell.settings.security.deleteAccount.agencyOtherMembers", {
                        count: a.memberCount - 1,
                      })}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ),
        confirmer: t("shell.settings.security.deleteAccount.agencyConfirmAction"),
        annuler: t("common.cancel"),
        closeLabel: t("common.close"),
        danger: true,
      });
      if (!okAgences) return;
    }
    await runDelete();
  }

  async function runDelete() {
    if (deleteState.kind === "loading") return;
    setDeleteState({ kind: "loading" });
    try {
      await deleteMyAccount();
      // Sign out clear le cookie session + reload → l'user retombe sur
      // LoginScreen. Pas besoin de feedback intermédiaire.
      await signOut();
    } catch (err) {
      setDeleteState({
        kind: "error",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  return (
    <>
      <div className="flex flex-col gap-3">
        <ActionDeSecurite
          titre={t("shell.settings.security.changePassword.title")}
          texte={
            resetState.kind === "sent" ? (
              t("shell.settings.security.changePassword.sentTo", {
                email: resetState.email,
              })
            ) : resetState.kind === "error" ? (
              <span className="text-erreur">{resetState.message}</span>
            ) : (
              t("shell.settings.security.changePassword.description")
            )
          }
          action={
            <RowButton
              label={
                resetState.kind === "loading"
                  ? t("common.loading")
                  : resetState.kind === "sent"
                    ? t("shell.settings.security.changePassword.resend")
                    : t("shell.settings.security.changePassword.action")
              }
              icon={
                resetState.kind === "loading" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Key className="h-4 w-4" />
                )
              }
              onClick={handleResetPassword}
              disabled={resetState.kind === "loading"}
            />
          }
        />
        <ActionDeSecurite
          titre={t("shell.settings.security.contactSupport.title")}
          texte={t("shell.settings.security.contactSupport.description")}
          action={
            <RowButton
              label={t("shell.settings.security.contactSupport.action")}
              icon={<Headphones className="h-4 w-4" />}
              href={SALON_DES_TICKETS}
            />
          }
        />
        <ActionDeSecurite
          titre={t("shell.settings.security.deleteAccount.title")}
          texte={
            deleteState.kind === "error" ? (
              <span className="text-erreur">{deleteState.message}</span>
            ) : (
              t("shell.settings.security.deleteAccount.description")
            )
          }
          action={
            <RowButton
              label={
                deleteState.kind === "loading"
                  ? t("common.loading")
                  : t("shell.settings.security.deleteAccount.action")
              }
              icon={
                deleteState.kind === "loading" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )
              }
              danger
              onClick={() => void handleDelete()}
              disabled={deleteState.kind === "loading"}
            />
          }
        />
      </div>
      {dialogue}
    </>
  );
}

/** Une action du compte : le titre et ce qu'elle fait, le bouton à droite — dessous quand la place manque. */
function ActionDeSecurite({
  titre,
  texte,
  action,
}: {
  titre: string;
  texte: ReactNode;
  action: ReactNode;
}) {
  return (
    <Carte className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
      <div className="min-w-[14rem] flex-1">
        <h3 className="text-[15px] font-medium text-fg">{titre}</h3>
        <p className="mt-0.5 text-[13px] leading-snug text-muted">{texte}</p>
      </div>
      {action}
    </Carte>
  );
}

/* ============================================================ */
/* PIECES                                                          */
/* ============================================================ */

interface ReadOnlyRowProps {
  label: string;
  value: string;
  copyable?: boolean;
  accent?: boolean;
}

/** Une info en lecture seule : l'étiquette à gauche, la valeur (et « copier ») à droite. */
function ReadOnlyRow({ label, value, copyable, accent }: ReadOnlyRowProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  function handleCopy() {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  }

  return (
    <Ligne
      etiquette={<span className="text-[13px] text-muted">{label}</span>}
      valeur={
        <span className="flex items-center gap-2">
          <span className={"truncate font-mono text-[13px] " + (accent ? "font-semibold" : "")}>
            {value}
          </span>
          {copyable && (
            <Infobulle texte={copied ? t("common.copied") : t("common.copy")}>
              <Button
                variant="ghost"
                size="md"
                iconOnly
                onClick={handleCopy}
                aria-label={t("shell.settings.account.copyAria")}
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-valide" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </Button>
            </Infobulle>
          )}
        </span>
      }
    />
  );
}

interface RowButtonProps {
  label: string;
  icon?: ReactNode;
  onClick?: () => void;
  /** Si fourni, la ligne devient un lien externe (rend un `<a target="_blank">`
   *  via le primitive Button) — fiable partout, contrairement a window.open. */
  href?: string;
  disabled?: boolean;
  danger?: boolean;
}

function RowButton({
  label,
  icon,
  onClick,
  href,
  disabled,
  danger,
}: RowButtonProps) {
  // Refonte 2026-05-28 : on delegue au primitive <Button>. danger -> rouge
  // plein (destructive), sinon gris (secondary).
  return (
    <Button
      variant={danger ? "destructive" : "secondary"}
      size="lg"
      onClick={onClick}
      href={href}
      target={href ? "_blank" : undefined}
      disabled={disabled}
      icon={icon}
    >
      {label}
    </Button>
  );
}

/**
 * Carré 96×96 cliquable pour upload (avatar, logo). Click → file picker
 * → upload → setStorage. Hover → "remove" badge si une photo custom est
 * déjà set. Fallback initiale si pas d'image.
 */
function ImageUploader({
  currentUrl,
  fallback,
  isCustom,
  onUpload,
  onClear,
  ariaLabel,
  uploadHint,
  clearLabel,
  disabled = false,
}: {
  currentUrl: string | null;
  fallback: string;
  isCustom: boolean;
  onUpload: (file: File) => Promise<void>;
  onClear: () => Promise<void>;
  ariaLabel: string;
  uploadHint: string;
  clearLabel: string;
  disabled?: boolean;
  shape?: "rounded" | "square";
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<"idle" | "uploading" | "clearing">("idle");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const initial = fallback.trim().charAt(0).toUpperCase() || "U";

  async function handlePick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // re-pick same file later
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError(t("shell.settings.uploader.notImage"));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError(t("shell.settings.uploader.tooBig"));
      return;
    }
    setError(null);
    setBusy("uploading");
    try {
      await onUpload(file);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy("idle");
    }
  }

  async function handleClear() {
    setError(null);
    setBusy("clearing");
    try {
      await onClear();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy("idle");
    }
  }

  const carre = (
    <Carte
      remplissage="aucun"
      onClick={() => !disabled && inputRef.current?.click()}
      disabled={disabled || busy !== "idle"}
      aria-label={ariaLabel}
      className={
        "group relative flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden " +
        (disabled ? "cursor-not-allowed opacity-60" : "")
      }
    >
      <Avatar src={currentUrl} nom={initial} taille={96} forme="arrondi" />
      {!disabled && (
        <span className="absolute inset-0 flex items-center justify-center bg-canvas/70 text-fg opacity-0 transition-opacity group-hover:opacity-100">
          {busy === "uploading" ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <ImageIcon className="h-5 w-5" />
          )}
        </span>
      )}
    </Carte>
  );

  return (
    <div className="flex flex-col items-start gap-1.5">
      {disabled ? carre : <Infobulle texte={uploadHint}>{carre}</Infobulle>}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handlePick}
        className="hidden"
      />
      {isCustom && !disabled && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClear}
          disabled={busy !== "idle"}
        >
          {busy === "clearing" ? t("common.loading") : clearLabel}
        </Button>
      )}
      {error && (
        <span className="max-w-24 break-words text-[12px] text-erreur" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
